import { useCallback, useEffect, useState } from 'react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { KpiStrip, MetricCard, Panel, SectionHeader } from '@/components/erp/primitives'
import { OutcomeStrip, RankedBars, StageBoard } from '@/shared/ui/charts'
import { useElectionContext } from '@/shared/election/context'
import { can as hasPermission, useSession } from '@/shared/auth/session'
import {
  LabeledBar, Pill, SeverityBadge, fmtDate, fmtKES, label,
} from '@/features/dossier/components/primitives'
import {
  closeOperation, getOperation, listOperations, listPlays, listTargets, moveOperationStage,
  openOperation, recordEvent, attachPlayToOperation,
  type Operation, type OperationDetail, type Play, type Target,
} from './api'

const STAGES = ['RECON', 'WEAPONIZE', 'DEPLOY', 'SUSTAIN', 'NEUTRALIZE']
const OUTCOMES = ['ACHIEVED', 'PARTIAL', 'NO_EFFECT', 'BACKFIRED', 'ABANDONED']
const EVENT_TYPES = ['NOTE', 'SPEND_RECORDED', 'EVIDENCE_LINKED', 'PAUSED', 'RESUMED']
const RISKS = ['LOW', 'MEDIUM', 'HIGH', 'EXTREME']

/** What each gate checks, said before the click rather than in the 422 after it. */
const GATES: Record<string, string> = {
  WEAPONIZE: 'two items from INDEPENDENT sources above the Admiralty floor on the target, or '
    + 'one primary public record',
  DEPLOY: 'every play attached to the target legally cleared, and a named accountable person',
  SUSTAIN: 'no linked item disputed or debunked since deploy',
  NEUTRALIZE: 'an outcome recorded — which is what Close it out does',
}

/**
 * Active operations — the board, the money, and the outcomes.
 *
 * ## Closing is the act that neutralises a target
 *
 * The kill chain refuses a bare "move to NEUTRALIZE" and says so: a target reaches the end by
 * having the operation run against it closed with an outcome. So the close control lives
 * here, and the service moves the target and writes a stage transition carrying the outcome.
 *
 * ## The charts are the point of the page, not decoration
 *
 * The stage board answers "where is everything", which a table of nine columns cannot.
 * `BACKFIRED` is a first-class slice of the outcome mix in its own colour — an outcome
 * vocabulary with no way to say "this hurt us" produces a campaign history in which nothing
 * ever went wrong, and a chart that hid the slice would do the same job more quietly.
 *
 * ## Budget is shown as a bar against its own budget, never as a bare number
 *
 * "Spent 1.4M" is unreadable; "1.4M of 2M" is a decision. An operation with no budget set
 * shows no bar rather than a full one — an unmeasured spend is not an overspend.
 */
export function OperationsPage() {
  const { electionId } = useElectionContext()
  const authorities = useSession((s) => s.authorities)
  const canOperate = hasPermission(authorities, 'opposition:operate')

  const [operations, setOperations] = useState<Operation[] | null>(null)
  const [targets, setTargets] = useState<Target[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [detail, setDetail] = useState<OperationDetail | null>(null)
  const [plays, setPlays] = useState<Play[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [opening, setOpening] = useState(false)
  const [closing, setClosing] = useState(false)
  const [stageFilter, setStageFilter] = useState<string | null>(null)

  // ── the open form ──────────────────────────────────────────────────────────
  const [name, setName] = useState('')
  const [objective, setObjective] = useState('')
  const [targetId, setTargetId] = useState('')
  const [teamLabel, setTeamLabel] = useState('')
  const [budgetKes, setBudgetKes] = useState('')
  const [riskLevelCode, setRiskLevelCode] = useState('MEDIUM')

  // ── the log and the close ──────────────────────────────────────────────────
  const [stageNote, setStageNote] = useState('')
  const [eventTypeCode, setEventTypeCode] = useState('NOTE')
  const [eventDetail, setEventDetail] = useState('')
  const [eventAmount, setEventAmount] = useState('')
  const [outcomeCode, setOutcomeCode] = useState('ACHIEVED')
  const [outcomeNote, setOutcomeNote] = useState('')

  const load = useCallback(async () => {
    try {
      setError(null)
      const rows = await listTargets()
      setTargets(rows)
      // Operations are keyed by election; the target list is how we discover which.
      const ids = [...new Set(rows.map((t) => t.electionId))]
      const pages = await Promise.all(ids.map((id) => listOperations(id)))
      setOperations(pages.flat())
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  const loadDetail = useCallback(async (id: string) => {
    try {
      const found = await getOperation(id)
      setDetail(found)
      setPlays(found.operation.targetId ? await listPlays(found.operation.targetId) : [])
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])
  useEffect(() => { if (selected) void loadDetail(selected) }, [selected, loadDetail])

  if (error && !operations) return <ErrorStrip message={error} />
  if (!operations) return <LoadingBlock label="Loading operations" />

  const open = operations.filter((o) => o.outcomeCode == null)
  const closed = operations.filter((o) => o.outcomeCode != null)
  const backfired = closed.filter((o) => o.outcomeCode === 'BACKFIRED').length
  const budgeted = operations.filter((o) => o.budgetKes != null)
  const totalBudget = budgeted.reduce((n, o) => n + (o.budgetKes ?? 0), 0)
  const totalSpent = operations.reduce((n, o) => n + (o.spentKes ?? 0), 0)

  const shown = stageFilter
    ? operations.filter((o) => o.stageCode === stageFilter) : operations
  const current = detail?.operation

  async function submitOpen(event: React.FormEvent) {
    event.preventDefault()
    if (!electionId || !name.trim() || !objective.trim()) return
    setBusy(true)
    setError(null)
    try {
      const saved = await openOperation({
        electionId,
        name: name.trim(),
        targetId: targetId || null,
        objective: objective.trim(),
        // The accountable person is the opener. The DEPLOY gate refuses an operation with
        // nobody's name on it, and a picker here would only invite naming somebody absent.
        accountableUserId: useSession.getState().user?.id,
        teamLabel: teamLabel.trim() || null,
        budgetKes: budgetKes.trim() ? Number(budgetKes) : null,
        riskLevelCode,
      })
      setOpening(false)
      setName(''); setObjective(''); setTeamLabel(''); setBudgetKes('')
      await load()
      setSelected(saved.id)
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  async function log() {
    if (!selected || !eventDetail.trim()) return
    setBusy(true)
    setError(null)
    try {
      await recordEvent(selected, {
        eventTypeCode,
        detail: eventDetail.trim(),
        amountKes: eventAmount.trim() ? Number(eventAmount) : null,
      })
      setEventDetail(''); setEventAmount('')
      await loadDetail(selected)
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  async function attach(playId: string) {
    if (!selected) return
    setBusy(true)
    setError(null)
    try {
      await attachPlayToOperation(selected, playId)
      await loadDetail(selected)
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  async function moveStage(stageCode: string) {
    if (!selected) return
    setBusy(true)
    setError(null)
    try {
      await moveOperationStage(selected, stageCode, stageNote.trim() || undefined)
      setStageNote('')
      await loadDetail(selected)
      await load()
    } catch (err) {
      // The 422 names the unmet gate verbatim; it is more useful than a paraphrase.
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  async function close() {
    if (!selected || !outcomeNote.trim()) return
    setBusy(true)
    setError(null)
    try {
      await closeOperation(selected, { outcomeCode, outcomeNote: outcomeNote.trim() })
      setClosing(false)
      setOutcomeNote('')
      await loadDetail(selected)
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Active Operations"
        description="Several operations can run against one target at once — each walks its
                     own five stages with its own gates, budget and outcome. Every one has a
                     named accountable person, and closing is what moves its target to
                     NEUTRALIZE."
      />

      {error && <ErrorStrip message={error} />}

      <KpiStrip>
        <MetricCard label="Open" value={open.length} sub="Not yet closed out" />
        <MetricCard label="Closed" value={closed.length} sub="With a recorded outcome" />
        <MetricCard label="Backfired" value={backfired}
                    sub="Counted, never quietly dropped" />
        <MetricCard label="Spent" value={fmtKES(totalSpent)}
                    sub={totalBudget > 0 ? `of ${fmtKES(totalBudget)} budgeted`
                      : 'no budgets set'} />
      </KpiStrip>

      <Panel>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border
                        p-2">
          <p className="text-[11px] font-medium">Where everything is</p>
          <div className="flex items-center gap-2">
            {stageFilter && (
              <button type="button" onClick={() => setStageFilter(null)}
                      className="text-[11px] text-muted-foreground underline">
                Clear the {label(stageFilter)} filter
              </button>
            )}
            {canOperate && (
              <button type="button" onClick={() => setOpening((v) => !v)}
                      className="rounded border border-border px-2 py-1 text-[11px]">
                {opening ? 'Close' : 'Open an operation'}
              </button>
            )}
          </div>
        </div>

        <div className="p-2">
          <StageBoard
            columns={STAGES.map((stage) => ({
              key: stage,
              label: label(stage),
              hint: `${operations.filter((o) => o.stageCode === stage).length} operation(s)`,
              items: operations.filter((o) => o.stageCode === stage),
            }))}
            onSelectColumn={(key) => setStageFilter(key === stageFilter ? null : key)}
            renderItem={(op: Operation) => (
              <button type="button" onClick={() => setSelected(op.id)}
                      className={`w-full rounded border p-1.5 text-left ${selected === op.id
                        ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'}`}>
                <span className="block font-mono text-[10px] text-muted-foreground">
                  {op.ref}
                </span>
                <span className="block text-[11px] font-medium">{op.name}</span>
                <span className="mt-0.5 flex flex-wrap items-center gap-1">
                  <SeverityBadge severity={op.riskLevelCode} />
                  {op.outcomeCode && (
                    <Pill className={op.outcomeCode === 'BACKFIRED'
                      ? 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-400'
                      : 'border-border'}>
                      {label(op.outcomeCode)}
                    </Pill>
                  )}
                </span>
              </button>
            )}
          />
        </div>
      </Panel>

      {opening && (
        <Panel>
          <form onSubmit={submitOpen} className="space-y-2 p-3">
            <p className="text-[12px] font-medium">Open an operation</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Name
                </span>
                <input value={name} onChange={(e) => setName(e.target.value)} required
                       placeholder="Westlands tender push"
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Against which target
                </span>
                <select value={targetId} onChange={(e) => setTargetId(e.target.value)}
                        className="h-7 rounded border border-input bg-background px-2
                                   text-[12px]">
                  <option value="">Not tied to one</option>
                  {targets.map((t) => (
                    <option key={t.id} value={t.id}>{t.displayName}</option>
                  ))}
                </select>
                <span className="text-[10px] text-muted-foreground">
                  {/* The consequence, said before the choice. */}
                  Closing this operation is what moves that target to NEUTRALIZE.
                </span>
              </label>
            </div>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Objective
              </span>
              <textarea value={objective} onChange={(e) => setObjective(e.target.value)} rows={2}
                        required
                        className="rounded border border-input bg-background px-2 py-1
                                   text-[12px]" />
            </label>
            <div className="grid gap-2 sm:grid-cols-3">
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Team label
                </span>
                <input value={teamLabel} onChange={(e) => setTeamLabel(e.target.value)}
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Budget (KES)
                </span>
                <input type="number" value={budgetKes}
                       onChange={(e) => setBudgetKes(e.target.value)}
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Risk
                </span>
                <select value={riskLevelCode} onChange={(e) => setRiskLevelCode(e.target.value)}
                        className="h-7 rounded border border-input bg-background px-2
                                   text-[12px]">
                  {RISKS.map((r) => <option key={r} value={r}>{label(r)}</option>)}
                </select>
              </label>
            </div>
            <p className="text-[11px] text-muted-foreground">
              You will be recorded as the accountable person. An operation nobody can be asked
              about is one the DEPLOY gate refuses.
            </p>
            <button type="submit" disabled={busy || !electionId || !name.trim()
              || !objective.trim()}
                    className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                               text-primary-foreground disabled:opacity-50">
              {busy ? 'Opening…' : 'Open it'}
            </button>
            {!electionId && (
              <p className="text-[11px] text-amber-600">
                Choose an election in the bar above first.
              </p>
            )}
          </form>
        </Panel>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <Panel>
          <div className="p-2">
            <p className="text-[11px] font-medium">How they ended</p>
            <OutcomeStrip
              className="mt-2"
              parts={OUTCOMES.map((code) => ({
                label: label(code),
                value: closed.filter((o) => o.outcomeCode === code).length,
                // BACKFIRED is red on purpose, and ABANDONED is not: one is a result, the
                // other is a decision to stop.
                tone: code === 'ACHIEVED' ? 'good'
                  : code === 'PARTIAL' ? 'warn'
                  : code === 'BACKFIRED' ? 'bad' : 'neutral',
              }))}
            />
          </div>
        </Panel>

        <Panel>
          <div className="p-2">
            <p className="text-[11px] font-medium">Spend, against what was budgeted</p>
            {budgeted.length === 0 ? (
              <p className="py-6 text-center text-[11px] text-muted-foreground">
                No budgets set. An unmeasured spend is not an overspend, so nothing is drawn.
              </p>
            ) : (
              <RankedBars
                className="mt-2"
                points={budgeted.map((o) => ({
                  label: `${o.ref} · ${o.name}`,
                  value: o.spentKes ?? 0,
                }))}
                max={Math.max(...budgeted.map((o) => o.budgetKes ?? 0), 1)}
                formatValue={(n) => fmtKES(n)}
                tone={(rowLabel) => {
                  const op = budgeted.find((o) => `${o.ref} · ${o.name}` === rowLabel)
                  if (!op?.budgetKes) return undefined
                  return (op.spentKes ?? 0) > op.budgetKes ? 'bad' : 'good'
                }}
              />
            )}
          </div>
        </Panel>
      </div>

      {shown.length > 0 && stageFilter && (
        <Panel>
          <p className="px-2 pt-2 text-[11px] text-muted-foreground">
            {shown.length} operation(s) at {label(stageFilter)}.
          </p>
        </Panel>
      )}

      {current && (
        <Panel>
          <div className="space-y-2 p-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="text-[13px] font-medium">
                  <span className="font-mono text-[11px] text-muted-foreground">
                    {current.ref}
                  </span>{' '}
                  {current.name}
                </p>
                <p className="text-[11px] text-muted-foreground">{current.objective}</p>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <Pill className="border-border">{label(current.stageCode)}</Pill>
                <Pill className="border-border">{label(current.statusCode)}</Pill>
                <SeverityBadge severity={current.riskLevelCode} />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              {current.budgetKes != null ? (
                <LabeledBar
                  label={`Spent ${fmtKES(current.spentKes)} of ${fmtKES(current.budgetKes)}`}
                  value={Math.min(100,
                    Math.round(((current.spentKes ?? 0) / current.budgetKes) * 100))}
                  tone={(current.spentKes ?? 0) > current.budgetKes
                    ? 'bg-rose-500' : 'bg-emerald-500'}
                />
              ) : (
                <p className="text-[11px] text-muted-foreground">
                  Spent {fmtKES(current.spentKes)} · no budget set
                </p>
              )}
              <p className="text-[11px] text-muted-foreground">
                {current.teamLabel ?? 'no team label'}
                {current.targetCompletionOn && ` · due ${fmtDate(current.targetCompletionOn)}`}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {detail.playIds.length} play(s) attached
              </p>
            </div>

            {/* THE OPERATION'S OWN CHAIN. Separate from the target's: several operations can
                run against one person at once, each at its own stage with its own gates, and
                this is where each one is walked along. */}
            {!current.outcomeCode && canOperate && (() => {
              const at = STAGES.indexOf(current.stageCode)
              const nextStage = at >= 0 && at < STAGES.length - 1 ? STAGES[at + 1] : null
              if (!nextStage) return null
              return (
                <div className="space-y-1.5 rounded border border-border p-2">
                  <div className="flex flex-wrap items-center gap-1">
                    {STAGES.map((stage, i) => (
                      <span key={stage} className="flex items-center gap-1">
                        <Pill className={i <= at
                          ? 'border-primary/40 bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground'}>
                          {label(stage)}
                        </Pill>
                        {i < STAGES.length - 1 && (
                          <span className="text-muted-foreground">›</span>
                        )}
                      </span>
                    ))}
                  </div>
                  {nextStage === 'NEUTRALIZE' ? (
                    <p className="text-[11px] text-muted-foreground">
                      This operation reaches Neutralize by being closed with an outcome —
                      the control is below.
                    </p>
                  ) : (
                    <>
                      <p className="text-[11px] text-muted-foreground">
                        Next: <b>{label(nextStage)}</b>. The gate checks {GATES[nextStage]}.
                      </p>
                      <div className="flex flex-wrap items-end gap-2">
                        <label className="flex flex-1 flex-col gap-0.5">
                          <span className="text-[10px] uppercase tracking-wide
                                           text-muted-foreground">
                            Note (optional, kept on the transition)
                          </span>
                          <input value={stageNote}
                                 onChange={(e) => setStageNote(e.target.value)}
                                 className="h-7 rounded border border-input bg-background px-2
                                            text-[12px]" />
                        </label>
                        <button type="button" disabled={busy}
                                onClick={() => void moveStage(nextStage)}
                                className="rounded bg-primary px-3 py-1.5 text-[12px]
                                           font-medium text-primary-foreground
                                           disabled:opacity-50">
                          {busy ? 'Checking the gate…' : `Move to ${label(nextStage)}`}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )
            })()}

            {current.outcomeCode ? (
              <div className="rounded border border-border p-2">
                <div className="flex items-center gap-2">
                  <Pill className={current.outcomeCode === 'BACKFIRED'
                    ? 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-400'
                    : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'}>
                    {label(current.outcomeCode)}
                  </Pill>
                  <span className="text-[11px] text-muted-foreground">
                    Closed. Its target was moved to Neutralize.
                  </span>
                </div>
                <p className="mt-1 text-[11px]">{current.outcomeNote}</p>
              </div>
            ) : canOperate && (
              <>
                {/* Attaching plays is what makes DEPLOY possible; the gate counts them. */}
                {plays.length > 0 && (
                  <div className="rounded border border-border p-2">
                    <p className="text-[11px] font-medium">Plays on this target</p>
                    <ul className="mt-1 space-y-1">
                      {plays.map((p) => {
                        const on = detail.playIds.includes(p.id)
                        return (
                          <li key={p.id} className="flex flex-wrap items-center gap-2
                                                     text-[11px]">
                            <span className="flex-1">{p.title}</span>
                            <Pill className="border-border">
                              {label(p.legalReviewStatusCode)}
                            </Pill>
                            {on ? (
                              <span className="text-[10px] text-muted-foreground">attached</span>
                            ) : (
                              <button type="button" disabled={busy}
                                      onClick={() => void attach(p.id)}
                                      className="rounded border border-border px-2 py-0.5
                                                 text-[10px] disabled:opacity-50">
                                Attach
                              </button>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )}

                <div className="flex flex-wrap items-end gap-2 rounded border border-border p-2">
                  <label className="flex flex-col gap-0.5">
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Log an event
                    </span>
                    <select value={eventTypeCode}
                            onChange={(e) => setEventTypeCode(e.target.value)}
                            className="h-7 rounded border border-input bg-background px-2
                                       text-[12px]">
                      {EVENT_TYPES.map((t) => <option key={t} value={t}>{label(t)}</option>)}
                    </select>
                  </label>
                  <label className="flex min-w-48 flex-1 flex-col gap-0.5">
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Detail
                    </span>
                    <input value={eventDetail} onChange={(e) => setEventDetail(e.target.value)}
                           className="h-7 rounded border border-input bg-background px-2
                                      text-[12px]" />
                  </label>
                  {eventTypeCode === 'SPEND_RECORDED' && (
                    <label className="flex flex-col gap-0.5">
                      <span className="text-[10px] uppercase tracking-wide
                                       text-muted-foreground">Amount (KES)</span>
                      <input type="number" value={eventAmount}
                             onChange={(e) => setEventAmount(e.target.value)}
                             className="h-7 rounded border border-input bg-background px-2
                                        text-[12px]" />
                    </label>
                  )}
                  <button type="button" disabled={busy || !eventDetail.trim()}
                          onClick={() => void log()}
                          className="rounded border border-border px-3 py-1.5 text-[12px]
                                     disabled:opacity-50">
                    Log it
                  </button>
                </div>

                <div className="rounded border border-border p-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-[11px] font-medium">Close it out</p>
                    <button type="button" onClick={() => setClosing((v) => !v)}
                            className="rounded border border-border px-2 py-0.5 text-[11px]">
                      {closing ? 'Not yet' : 'Close with an outcome'}
                    </button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    This is the act that moves{' '}
                    {targets.find((t) => t.id === current.targetId)?.displayName
                      ?? 'its target'} to NEUTRALIZE, with the outcome written into the stage
                    history.
                  </p>
                  {closing && (
                    <div className="mt-1.5 space-y-1.5">
                      <div className="flex flex-wrap items-end gap-2">
                        <label className="flex flex-col gap-0.5">
                          <span className="text-[10px] uppercase tracking-wide
                                           text-muted-foreground">Outcome</span>
                          <select value={outcomeCode}
                                  onChange={(e) => setOutcomeCode(e.target.value)}
                                  className="h-7 rounded border border-input bg-background px-2
                                             text-[12px]">
                            {OUTCOMES.map((o) => <option key={o} value={o}>{label(o)}</option>)}
                          </select>
                        </label>
                        <span className="pb-1 text-[10px] text-muted-foreground">
                          {/* Said out loud, because the temptation is to never pick it. */}
                          Backfired is a real answer and is counted as one.
                        </span>
                      </div>
                      <label className="flex flex-col gap-0.5">
                        <span className="text-[10px] uppercase tracking-wide
                                         text-muted-foreground">
                          What happened (required)
                        </span>
                        <textarea value={outcomeNote} rows={2}
                                  onChange={(e) => setOutcomeNote(e.target.value)}
                                  className="rounded border border-input bg-background px-2
                                             py-1 text-[12px]" />
                      </label>
                      <button type="button" disabled={busy || !outcomeNote.trim()}
                              onClick={() => void close()}
                              className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                                         text-primary-foreground disabled:opacity-50">
                        {busy ? 'Closing…' : 'Close the operation'}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}

            <div>
              <p className="text-[11px] font-medium">Log</p>
              {detail.log.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">Nothing logged yet.</p>
              ) : (
                <ul className="mt-1 space-y-0.5">
                  {detail.log.map((e) => (
                    <li key={e.id} className="flex flex-wrap items-baseline gap-2 text-[11px]">
                      <Pill className="border-border">{label(e.eventTypeCode)}</Pill>
                      <span className="flex-1">{e.detail}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {fmtDate(e.occurredAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Panel>
      )}
    </div>
  )
}
